import { renderReadOnlyRating } from '../../common/rating';
import { MenuItem, Variety } from '../../types';

type Filter = 'name' | 'thc' | 'cbd' | 'gram' | 'eighth';

declare const STATIC_URL: string;

interface Sort {
    filter: Filter;
    order: 'asc' | 'des';
}

export default function DispensaryMenu(menuItems: MenuItem[]) {
    renderMenu(menuItems, { filter: 'thc', order: 'des' });
    initMenuTabs(menuItems);
    initTableSorters(menuItems);
}

function renderMenu(menuItems: MenuItem[], sort: Sort) {
    const sortedItems = sortMenuItems(menuItems, sort);
    const menuArr = ['indicas-menu', 'sativas-menu', 'hybrids-menu'].map(
        (id, index) => ({
            el: document.getElementById(id),
            items: sortedItems[index],
        })
    );

    for (const menu of menuArr) {
        if (!menu.el) continue;
        menu.el.innerHTML = menu.items
            .map((item) => getMenuItemHtml(item))
            .join('');
    }

    renderMenuItemRatings();
    updateSorterIcons(sort);
}

const upper = (str: string) => str[0].toUpperCase() + str.slice(1);

function populateTabs(menuTabs: HTMLElement, types: Variety[]) {
    menuTabs.innerHTML = types
        .map(
            (variety) => `
<li class="hidden" role="presentation">
    <a id="${variety}-tab" data-toggle="tab" role="tab"
        aria-controls="home" aria-selected="true">
        <img src="${STATIC_URL}images/variety-${variety}.svg"
            alt="${upper(variety)} Strain Variety"
            width="24" height="24">
        <span class="tab-title">${upper(variety)}</span>
    </a>
</li>`
        )
        .join('');
}

function initMenuTabs(menuItems: MenuItem[]) {
    const menuTabs = document.getElementById('menu-tabs');
    const types: Variety[] = ['indica', 'sativa', 'hybrid'];
    if (menuTabs) populateTabs(menuTabs, types);

    const available = types.filter(
        (type) => getMenuItemsByType(menuItems, type).length !== 0
    );

    for (const tab of available) {
        const tabEl = document.getElementById(`${tab}-tab`)?.parentElement;
        tabEl?.classList.remove('hidden');
    }

    showMenuTab(available[0]);
    setActiveTab($('#' + available[0] + '-tab'));
    registerMenuTabHandlers(types);
}

function registerMenuTabHandlers(types: Variety[]) {
    for (const variety of types) {
        $(`#${variety}-tab`).on('click', (e) => {
            setActiveTab($(e.currentTarget));
            showMenuTab(variety);
        });
    }
}

function showMenuTab(variety: Variety) {
    $('#indicas-menu').addClass('hidden');
    $('#sativas-menu').addClass('hidden');
    $('#hybrids-menu').addClass('hidden');

    $(`#${variety}s-menu`).removeClass('hidden');

    const strainHeaderEl = document.getElementById('strain-header');
    let titleEl = strainHeaderEl?.querySelector('span');
    if (!titleEl) {
        titleEl = document.createElement('span');
        strainHeaderEl?.prepend(titleEl);
    }
    const capitalVariety = variety[0].toUpperCase() + variety.slice(1);
    titleEl.textContent = `${capitalVariety} Strains`;
}

function setActiveTab($activeTabLink: JQuery) {
    $activeTabLink
        .parent()
        .parent('ul')
        .children('li')
        .each(function () {
            $(this).removeClass('active');
        });
    $activeTabLink.parent().addClass('active');
}

const sorters: { id: string; filter: Filter }[] = [
    { id: 'strain-header', filter: 'name' },
    { id: 'thc-header', filter: 'thc' },
    { id: 'cbd-header', filter: 'cbd' },
    { id: 'gram-header', filter: 'gram' },
    { id: 'eighth-header', filter: 'eighth' },
];

function initTableSorters(menuItems: MenuItem[]) {
    for (const sorter of sorters) {
        const el = document.getElementById(sorter.id);
        el?.addEventListener('click', () => {
            const order = el.dataset.order === 'asc' ? 'des' : 'asc';
            renderMenu(menuItems, { filter: sorter.filter, order });
        });
    }
}

function updateSorterIcons(sort: Sort) {
    for (const sorter of sorters) {
        const el = document.getElementById(sorter.id);
        if (!el) continue;

        let iconClasses: string[];
        if (sorter.filter === sort.filter) {
            iconClasses =
                sort.order === 'asc' ? ['fa-sort-asc'] : ['fa-sort-desc'];
        } else {
            iconClasses = ['fa-sort', 'text-grey'];
        }

        el.querySelector('i')?.remove();
        const icon = document.createElement('i');
        icon.classList.add('fa', ...iconClasses, 'ml-2');
        el.append(icon);

        if (sorter.filter === sort.filter) {
            el.dataset.order = sort.order;
        } else {
            delete el.dataset.order;
        }
    }
}

function sortMenuItems(menuItems: MenuItem[], sort: Sort) {
    return (['indica', 'sativa', 'hybrid'] as Variety[]).map((type) => {
        const items = getMenuItemsByType(menuItems, type);
        return items.sort(createMenuSorter(sort));
    });
}

function createMenuSorter(sort: Sort) {
    return function (aItem: MenuItem, bItem: MenuItem) {
        let a, b;
        switch (sort.filter) {
            case 'name':
                a = aItem.strain_name;
                b = bItem.strain_name;
                break;
            case 'thc':
                a = aItem.strain_cannabinoids.THC;
                b = bItem.strain_cannabinoids.THC;
                break;
            case 'cbd':
                a = aItem.strain_cannabinoids.CBD;
                b = bItem.strain_cannabinoids.CBD;
                break;
            case 'gram':
                a = aItem.price_gram;
                b = bItem.price_gram;
                break;
            case 'eighth':
                a = aItem.price_eighth;
                b = bItem.price_eighth;
                break;
            default:
                a = aItem.strain_overall_rating;
                b = bItem.strain_overall_rating;
                break;
        }
        if (a === b) {
            return 0;
        } else if (a === null) {
            return 1;
        } else if (b === null) {
            return -1;
        } else if (sort.order === 'asc') {
            return a < b ? -1 : 1;
        } else {
            return a < b ? 1 : -1;
        }
    };
}

function getMenuItemHtml(menuItem: MenuItem) {
    return `<tr>
                <th>
                    <a href="${getStrainUrl(menuItem)}">
                        ${menuItem.strain_name}
                    </a>
                </th>
                <td>${formatScore(menuItem.strain_cannabinoids.THC)}</td>
                <td>${formatScore(menuItem.strain_cannabinoids.CBD)}</td>
                <td>${formatPrice(menuItem.price_gram)}</td>
                <td>${formatPrice(menuItem.price_eighth)}</td>
                <td>${formatScore(menuItem.match_score)}</td>
                <td>
                    <span class="review-stars-region">
                        ${formatRating(menuItem.strain_overall_rating)}
                    </span>
                </td>
            </tr>`;
}

function getMenuItemsByType(menuItems: MenuItem[], type: Variety) {
    return menuItems.filter((item) => item.strain_variety === type);
}

function getStrainUrl(item: MenuItem) {
    if (/\/kiosk/.test(window.location.pathname)) {
        const urlRegex = /[^/]+(?=\/$|$)/;
        const urlTail = item.url.match(urlRegex)?.[0];
        const url = `${window.location.pathname}${item.strain_id}-${urlTail}`;
        return url;
    }
    return item.url;
}

function formatPrice(price: number | null) {
    return price ? `$${price}` : '-';
}

function formatRating(rating: number | null) {
    return rating ? rating : 'Not Rated';
}

function formatScore(score: number | null) {
    return score ? `${score}%` : '-';
}

function renderMenuItemRatings() {
    const starsRegions = document.querySelectorAll(
        '.review-stars-region'
    ) as NodeListOf<HTMLElement>;

    for (const region of starsRegions) {
        const rating = region.innerText;
        if (!rating) {
            region.innerText = 'Not Rated';
        } else {
            renderReadOnlyRating(region, { rating: parseInt(rating) });
        }
    }
}
